import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BellRing, FlaskConical, Loader2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { NotificationAPI, extractError } from "@/lib/api";
import { formatRelativeTime } from "@/lib/format";
import { toast } from "sonner";

const LABELS = {
    "job.awaiting_approval": "Approval requested",
    "job.awaiting_parts": "Waiting for parts",
    "job.qc": "Sent to QC",
    "job.ready": "Ready for pickup",
    "job.delivered": "Delivered",
};

export default function NotificationsPage() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        NotificationAPI.list({ page_size: 100 })
            .then(setData)
            .catch((e) => toast.error(extractError(e)))
            .finally(() => setLoading(false));
    }, []);

    return (
        <div className="space-y-5 animate-fade-in" data-testid="notifications-page">
            <header className="flex items-start justify-between">
                <div>
                    <h1 className="font-display text-3xl font-extrabold tracking-tight sm:text-4xl">Notifications</h1>
                    <p className="mt-1 text-sm text-zinc-500">
                        Customer-facing alerts triggered by job-card transitions.
                    </p>
                </div>
            </header>

            <Card className="flex items-start gap-3 border-brand-redSoft bg-brand-redSoft/70 p-4 dark:border-brand-red/30 dark:bg-brand-red/10" data-testid="mock-banner">
                <FlaskConical className="mt-0.5 h-5 w-5 text-brand-redDeep dark:text-brand-red" />
                <div>
                    <div className="font-display text-sm font-bold tracking-tight text-brand-redDeep dark:text-brand-red">MOCK MODE</div>
                    <div className="text-xs text-zinc-700 dark:text-zinc-200">
                        Notifications currently use a stub adapter — nothing is actually sent yet. A real WhatsApp / SMS / Email provider can be wired in later. The rows below are persisted for audit and UI preview.
                    </div>
                </div>
            </Card>

            <Card className="p-0">
                {loading ? (
                    <div className="grid h-32 place-items-center"><Loader2 className="h-5 w-5 animate-spin text-zinc-400" /></div>
                ) : (data?.items || []).length === 0 ? (
                    <div className="grid h-32 place-items-center text-sm text-zinc-500">
                        <span className="flex items-center gap-2"><BellRing className="h-4 w-4" /> No notifications yet — transition a job to a customer-visible stage.</span>
                    </div>
                ) : (
                    <ul className="divide-y divide-zinc-100 dark:divide-zinc-800">
                        {data.items.map((n) => (
                            <li key={n.id} className="flex items-start gap-3 p-3 text-sm">
                                <div className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-brand-redSoft text-brand-redDeep dark:bg-brand-red/15 dark:text-brand-red">
                                    <BellRing className="h-4 w-4" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center justify-between gap-3">
                                        <span className="truncate font-medium">{LABELS[n.template_key] || n.template_key}</span>
                                        <span className="shrink-0 font-mono text-[10px] uppercase tracking-wider text-zinc-500">{n.channel}</span>
                                    </div>
                                    <div className="font-mono text-[11px] text-zinc-500">
                                        {n.job_number ? <Link to={`/jobs/${n.job_card_id}`} className="hover:underline">{n.job_number}</Link> : "—"} · to {n.to || "no contact"}
                                    </div>
                                    <div className="mt-0.5 font-mono text-[10px] text-zinc-400">
                                        {formatRelativeTime(n.created_at)} · status={n.status} · provider={n.provider}
                                    </div>
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
            </Card>
        </div>
    );
}
